<script setup lang="ts">
    import { PlanType } from '@/views/plan/store/types';
    import { usePlansStore } from '@/views/plan/store/store';
    import { computed, ref } from 'vue';
    import PlanManualDownloadWarningModal from '@/views/plan/PlanManualDownloadWarningModal.vue';
    import type { ApiPlan, ApiPlansCase } from '@/views/plan/api/types';
    import { assert } from '@/util/assert';
    import { verify } from '@/util/verify';
    import { planState } from '@/views/plan/store/planState';
    import { DateTime } from 'luxon';
    import { trackEvent } from '@/plugins/amplitude';
    import { caseEventProperties, planEventProperties } from '@/plugins/amplitudeEvent';
    import PlanOrderSurgicalGuide from '@/views/plan/PlanOrderSurgicalGuide.vue';
    import { PLANS_TARGETS } from '@/domain/analytics/analyticsTargets';
    import { useOrganisationProfile } from '@/stores/organisationProfile/store';
    import { useUserStore } from '@/stores/userStore';
    import { useSurgicalGuideOrders } from '@/stores/surgicalGuideOrders/store';

    const props = defineProps<Props>();
    const store = usePlansStore();
    const organisationProfile = useOrganisationProfile();
    const userStore = useUserStore();
    const ordersStore = useSurgicalGuideOrders();

    export interface Props {
        project: ApiPlansCase;
        plan: ApiPlan;
    }

    const showWarningDialog = ref(false);
    const isDownloadingPdf = ref(false);
    const isDownloadingModels = ref(false);

    const isAutomated = computed(() => {
        return props.plan.type === PlanType.Automated;
    });

    const isManual = computed(() => {
        return props.plan.type === PlanType.Manual;
    });

    const onAutomatedPlanDownload = async () => {
        assert(isAutomated.value, 'Automated plan download called for manual plan');
        isDownloadingPdf.value = true;
        try {
            await store.downloadAutomatedPlan();
        } finally {
            await trackEvent('plan_downloaded', {
                ...caseEventProperties({
                    projectId: props.project.id.toString(),
                    // surgicalTemplateHistoryId: store.state.automatedTemplate.id.toString(),
                }),
                ...planEventProperties(props.plan.id.toString(), true),
            });
            isDownloadingPdf.value = false;
        }
    };

    const onManualPlanDownloadIntent = async () => {
        assert(isManual.value, 'Manual plan download intent called for automated plan');
        const report = verify(
            props.plan.report,
            'Manual plan download intent called for plan without report',
        );

        if (report.include_latest_changes) {
            await onManualDownload();
        } else {
            showWarningDialog.value = true;
        }
    };

    const onDownloadModels = async () => {
        isDownloadingModels.value = true;
        try {
            await store.downloadModels(props.plan);
        } finally {
            await trackEvent('models_downloaded', {
                ...caseEventProperties({
                    projectId: props.project.id.toString(),
                    // surgicalTemplateHistoryId: isAutomated.value ?
                    //     store.state.automatedTemplate.id.toString() : store.state.manualTemplate?.id.toString(),
                }),
                ...planEventProperties(props.plan.id.toString(), isAutomated.value),
            });
            isDownloadingModels.value = false;
        }
    };

    const closeWarningDialog = () => {
        showWarningDialog.value = false;
    };

    const onDownloadFromWarningDialog = async () => {
        closeWarningDialog();
        await onManualDownload();
    };

    const onManualDownload = async () => {
        isDownloadingPdf.value = true;
        try {
            await store.downloadManualPlan();
        } finally {
            await trackEvent('plan_downloaded', {
                ...caseEventProperties({
                    projectId: props.project.id.toString(),
                    // surgicalTemplateHistoryId: store.state.manualTemplate?.id.toString(),
                }),
                ...planEventProperties(props.plan.id.toString(), false),
            });
            isDownloadingPdf.value = false;
        }
    };

    const isCreating = computed((): boolean => {
        const state = planState(props.plan.state);
        return (state === 'new' || state === 'processing') && !isStale.value;
    });

    const isCreationBroken = computed((): boolean => {
        const state = planState(props.plan.state);
        return state === 'error' || (state !== 'completed' && isStale.value);
    });

    const isStale = computed((): boolean => {
        const date = createdOn(props.plan);
        return !!date && date.plus({ day: 1 }) < DateTime.utc();
    });

    const createdOn = (plan: ApiPlan): DateTime => {
        return DateTime.fromISO(plan.created_on, { zone: 'utc' });
    };

    // Sales can place a guide order on manual plans; they also need to see the
    // card to act on an existing order (e.g. add a CR to a requested one).
    const hasSurgicalGuideOrder = computed(() => ordersStore.getPlanOrder(props.plan.id) !== null);

    // A guide is cut to this plan, so it needs the surgeon's decision behind it. The API refuses
    // an order against a proposal; this keeps the user from meeting that refusal after clicking.
    const planIsApproved = computed(() => props.plan.approval_state === 'approved');

    const roleMayOrderSurgicalGuide = computed((): boolean => {
        return (
            userStore.mayOrderSurgicalGuide ||
            (userStore.isSales && (isManual.value || hasSurgicalGuideOrder.value))
        );
    });

    const canOrderSurgicalGuide = computed(
        (): boolean => planIsApproved.value && roleMayOrderSurgicalGuide.value,
    );
</script>

<template>
    <div class="app-plan-actions">
        <div class="app-plan-download-buttons d-flex align-center">
            <app-button-secondary
                v-if="plan.files.length"
                class="mt-2"
                :loading="isDownloadingModels"
                data-testid="download-raw-models-button"
                :data-analytics-id="PLANS_TARGETS.rawButton"
                @click="onDownloadModels"
            >
                Download Raw Models
            </app-button-secondary>
            <v-spacer v-if="plan.files.length" />

            <PlanOrderSurgicalGuide
                v-if="
                    organisationProfile.features.order_surgical_guide &&
                    plan.report &&
                    canOrderSurgicalGuide
                "
                :data-analytics-id="PLANS_TARGETS.psiButton"
                :loading="isCreating || isCreationBroken"
                :case-id="project.id"
                :plan-id="plan.id"
            />

            <v-spacer />
            <app-button
                v-if="plan.report"
                :loading="isDownloadingPdf"
                data-testid="download-pdf-button"
                :data-analytics-id="PLANS_TARGETS.downloadReportButton"
                @click="isAutomated ? onAutomatedPlanDownload() : onManualPlanDownloadIntent()"
            >
                Download PDF
            </app-button>
            <v-card
                v-else-if="isCreating || isCreationBroken"
                width="244"
                rounded="lg"
                variant="tonal"
                class="px-4 py-2 text-secondary text-caption d-block"
            >
                <div v-if="isCreating">
                    <v-progress-linear color="primary" class="mt-2" indeterminate height="6" />
                    <br />
                    Your downloadable Plan is being created, this process can take up to 10 minutes.
                </div>
                <div v-else-if="isCreationBroken">
                    <v-icon color="error" size="40" class="w-100 mb-5 d-flex">
                        mdi-alert-circle-outline
                    </v-icon>
                    <span class="small-text text-error">
                        There was an error creating your Plan
                    </span>
                    <span class="small-text text-planner-grey">
                        Please
                        <a href="https://support.formuslabs.com/hc/en-us" target="_blank">
                            contact support
                        </a>
                        for assistance
                    </span>
                </div>
            </v-card>

            <PlanManualDownloadWarningModal
                v-if="isManual && plan.report && !plan.report.include_latest_changes"
                :value="showWarningDialog"
                @close="closeWarningDialog"
                @submit="onDownloadFromWarningDialog"
            />
        </div>
    </div>
</template>

<style scoped lang="scss">
    .app-plan-actions {
        flex-basis: 260px;
        flex-grow: 0;
    }

    .app-plan-card-ref {
        color: var(--planner-grey-2);
        text-transform: uppercase;
        font-weight: 700;
        font-size: 0.75rem;
        margin-bottom: 20px;
    }

    .app-plan-card-ref-number {
        color: rgb(var(--v-theme-secondary));
    }
</style>
